// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { Injectable } from '@angular/core';
import {
  Productivity,
  ContributionProject,
  ProjectsWeContributeCodeToResult,
  BaseProjectContributionInput,
  ContributorsGrowthResponse,
  ContributingCountriesResponse,
  Top10OrgsByCommitsResponse,
  AllOrgsContributionsResponse,
  TotalCommitsByOrgResponse,
  IssuesByOrgsResponse,
  PRsMergedByOrgsResponse,
  SubProjectsStatsResponse,
  DBTInput,
  ContributorLeaderboardResponse,
  OrganizationLeaderboardResponse,
  ContributorTeam,
  WorkTimeDistribution,
  PaginatedCommits,
  RepositoryContributions,
} from '@models';
import { Observable } from 'rxjs';
import { ProjectContributionsServiceGql } from '../gql';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environment';

@Injectable({
  providedIn: 'root',
})
export class ProjectContributionsService {
  membershipToProjectRoute = null;

  constructor(
    private projectContributionServiceGql: ProjectContributionsServiceGql,
    private http: HttpClient
  ) {}

  public getAllProjects(
    organizationId: string,
    dateRange: string,
    isAllOrgs: boolean
  ): Observable<ContributionProject[]> {
    return this.projectContributionServiceGql.getAllProjects(
      organizationId,
      dateRange,
      isAllOrgs
    );
  }

  public getProductivity(
    organizationId?: string,
    projectId?: string,
    dateRange?: string,
    isAllOrgs?: boolean
  ): Observable<Productivity[]> {
    return this.projectContributionServiceGql.getProductivity(
      organizationId,
      projectId,
      dateRange,
      isAllOrgs
    );
  }

  public getProjectsWeContributeCodeTo(
    organizationId?: string,
    foundationId?: string,
    projectId?: string,
    dateRange?: string,
    pageSize?: number,
    offset?: number
  ): Observable<ProjectsWeContributeCodeToResult> {
    return this.projectContributionServiceGql.getProjectsWeContributeCodeTo(
      organizationId,
      foundationId,
      projectId,
      dateRange,
      pageSize,
      offset
    );
  }

  getContributorsGrowth(
    input: BaseProjectContributionInput
  ): Observable<ContributorsGrowthResponse> {
    return this.projectContributionServiceGql.getContributorsGrowth(input);
  }

  getContributingCountries(
    input: BaseProjectContributionInput
  ): Observable<ContributingCountriesResponse> {
    return this.projectContributionServiceGql.getContributingCountries(input);
  }

  getTop10OrgsByCommits(
    input: BaseProjectContributionInput
  ): Observable<Top10OrgsByCommitsResponse> {
    return this.projectContributionServiceGql.getTop10OrgsByCommits(input);
  }

  getAllOrgsContributions(
    input: BaseProjectContributionInput
  ): Observable<AllOrgsContributionsResponse> {
    return this.projectContributionServiceGql.getAllOrgsContributions(input);
  }

  getTotalCommitsByOrgs(
    input: BaseProjectContributionInput
  ): Observable<TotalCommitsByOrgResponse> {
    return this.projectContributionServiceGql.getTotalCommitsByOrgs(input);
  }

  getIssuesByOrgs(
    input: BaseProjectContributionInput
  ): Observable<IssuesByOrgsResponse> {
    return this.projectContributionServiceGql.getIssuesByOrgs(input);
  }

  getPRsMergedByOrgs(
    input: BaseProjectContributionInput
  ): Observable<PRsMergedByOrgsResponse> {
    return this.projectContributionServiceGql.getPRsMergedByOrgs(input);
  }

  getContributorLeaderboard(
    input: BaseProjectContributionInput,
    noCache = false
  ): Observable<ContributorLeaderboardResponse> {
    return this.projectContributionServiceGql.getContributorLeaderboard(
      input,
      noCache
    );
  }

  getOrganizationLeaderboard(
    input: BaseProjectContributionInput,
    noCache = false
  ): Observable<OrganizationLeaderboardResponse> {
    return this.projectContributionServiceGql.getOrganizationLeaderboard(
      input,
      noCache
    );
  }

  getSubProjectsStats(
    input: BaseProjectContributionInput
  ): Observable<SubProjectsStatsResponse> {
    return this.projectContributionServiceGql.getSubProjectsStats(input);
  }

  getMailingLists(input: DBTInput) {
    return this.projectContributionServiceGql.getMailingLists(input);
  }

  getBoardMeetings(input: DBTInput) {
    return this.projectContributionServiceGql.getBoardMeetings(input);
  }

  getContributorTeam(
    organizationId: string,
    filters: {
      isMaintainer?: boolean;
      isContributor?: boolean;
      isCollaborator?: boolean;
      currentEmployeesOnly?: boolean;
    },
    projectId?: string,
    dateRange?: string,
    isAllOrgs?: boolean
  ): Observable<ContributorTeam[]> {
    const { isMaintainer, isContributor, isCollaborator, currentEmployeesOnly } =
      filters;

    return this.projectContributionServiceGql.getContributorTeam(
      organizationId,
      projectId,
      dateRange,
      isMaintainer,
      isContributor,
      isCollaborator,
      currentEmployeesOnly,
      isAllOrgs
    );
  }

  getWorkTimeDistribution(
    organizationId: string,
    projectId?: string,
    dateRange?: string,
    isAllOrgs?: boolean
  ): Observable<WorkTimeDistribution[]> {
    return this.projectContributionServiceGql.getWorkTimeDistribution(
      organizationId,
      projectId,
      dateRange,
      isAllOrgs
    );
  }

  getCodeCommits(
    organizationId: string,
    projectId?: string,
    dateRange = 'last_12mo',
    offset = 0,
    limit = 100,
    isAllOrgs = false,
    search = '',
    sortDirection: 'asc' | 'desc' = 'desc'
  ): Observable<PaginatedCommits> {
    let params = new HttpParams()
      .set('organizationId', organizationId)
      .set('projectId', projectId || '')
      .set('dateRange', dateRange)
      .set('offset', offset.toString())
      .set('limit', limit.toString())
      .set('isAllOrgs', isAllOrgs.toString())
      .set('sortDirection', sortDirection);

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<PaginatedCommits>(
      `${environment.apiUrl}/code-contributions/commits`,
      {
        params,
      }
    );
  }

  getRepositoryContributions(
    organizationId: string,
    projectId?: string,
    dateRange = 'last_12mo',
    isAllOrgs = false
  ): Observable<RepositoryContributions> {
    const params = new HttpParams()
      .set('organizationId', organizationId)
      .set('projectId', projectId || '')
      .set('dateRange', dateRange)
      .set('isAllOrgs', isAllOrgs.toString());

    return this.http.get<RepositoryContributions>(
      `${environment.apiUrl}/code-contributions/repositories`,
      { params }
    );
  }
}
